import React from "react";

/** Inline markdown: **grassetto**, *corsivo* / _corsivo_. */
function renderInline(text, keyBase) {
  const nodes = [];
  const regex = /(\*\*[^*]+\*\*|\*[^*\n]+\*|_[^_\n]+_)/g;
  let last = 0;
  let m;
  let i = 0;
  while ((m = regex.exec(text)) !== null) {
    if (m.index > last) nodes.push(text.slice(last, m.index));
    const tok = m[0];
    if (tok.startsWith("**")) {
      nodes.push(<strong key={`${keyBase}-b${i}`} className="font-semibold text-[#0f292f]">{tok.slice(2, -2)}</strong>);
    } else {
      nodes.push(<em key={`${keyBase}-i${i}`}>{tok.slice(1, -1)}</em>);
    }
    last = m.index + tok.length;
    i += 1;
  }
  if (last < text.length) nodes.push(text.slice(last));
  return nodes;
}

/**
 * Rende il contenuto di un articolo con formattazione leggera:
 * - "## Titolo" / "# Titolo"  → capitoli
 * - "- " oppure "• "          → elenco puntato
 * - "1. "                     → elenco numerato
 * - **grassetto**, *corsivo*  → inline
 * - riga isolata *…*          → nota in corsivo
 */
export default function ArticleContent({ text, className = "" }) {
  if (!text) return null;
  const blocks = text.replace(/\r\n/g, "\n").split(/\n{2,}/);

  return (
    <div className={className}>
      {blocks.map((block, bi) => {
        const trimmed = block.trim();
        if (!trimmed) return null;

        // Capitolo
        if (/^#{1,3}\s+/.test(trimmed)) {
          const level = trimmed.match(/^#+/)[0].length;
          const content = trimmed.replace(/^#{1,3}\s+/, "");
          const cls = level <= 1 ? "text-2xl" : "text-xl";
          return (
            <h3 key={bi} className={`${cls} font-bold text-[#0f292f] mt-9 mb-3 leading-tight`}>
              {renderInline(content, `h${bi}`)}
            </h3>
          );
        }

        const lines = trimmed.split("\n");

        // Elenco puntato
        if (lines.every((l) => /^\s*[-•]\s+/.test(l))) {
          return (
            <ul key={bi} className="list-disc pl-5 space-y-2 my-5 text-[#444] leading-relaxed">
              {lines.map((l, li) => (
                <li key={li}>{renderInline(l.replace(/^\s*[-•]\s+/, ""), `u${bi}-${li}`)}</li>
              ))}
            </ul>
          );
        }

        // Elenco numerato
        if (lines.every((l) => /^\s*\d+\.\s+/.test(l))) {
          return (
            <ol key={bi} className="list-decimal pl-5 space-y-2 my-5 text-[#444] leading-relaxed">
              {lines.map((l, li) => (
                <li key={li}>{renderInline(l.replace(/^\s*\d+\.\s+/, ""), `o${bi}-${li}`)}</li>
              ))}
            </ol>
          );
        }

        // Nota interamente in corsivo (es. disclaimer *...*)
        if (/^\*[^*]+\*$/.test(trimmed)) {
          return (
            <p key={bi} className="text-sm text-[#888] italic border-t border-gray-100 pt-4 mt-6">
              {trimmed.slice(1, -1)}
            </p>
          );
        }

        // Paragrafo normale (a-capo singoli preservati)
        return (
          <p key={bi} className="text-base text-[#444] leading-[1.85] mb-4">
            {lines.map((l, li) => (
              <React.Fragment key={li}>
                {li > 0 && <br />}
                {renderInline(l, `p${bi}-${li}`)}
              </React.Fragment>
            ))}
          </p>
        );
      })}
    </div>
  );
}
